import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Row, Col } from 'react-bootstrap';
import { OtpGenerateService, OtpValidateService } from '../../domain';

// Styled components for the modal
const ModalBackground = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(159, 159, 159, 0.5); /* Similar to SweetAlert's backdrop */
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1001;
`;

const ModalContainer = styled.div`
    background: rgb(234, 222, 208, 1); /* Modal background color */
    border-radius: 10px;
    padding: 30px;
    width: 400px;
    max-width: 90%;
    text-align: center;
    position: relative;
`;

const StyledModalHeader = styled.h2`
    color: rgba(41, 37, 97, 1);
    font-weight: 700;
    font-size: 24px;
    font-family: 'Montserrat', sans-serif;
    margin-bottom: 10px;
`;

const DescriptionText = styled.p`
    color: rgba(58, 57, 57, 1);
    font-weight: 500;
    font-size: 14px;
    font-family: 'Montserrat', sans-serif;
`;

const StyledInput = styled.input`
    width: 200px;
    padding: 10px;
    border-radius: 10px;
    border: 1px solid rgba(41, 37, 97, 0.6);
    font-family: 'Montserrat', sans-serif;
    font-size: 14px;
    text-align: center;
    letter-spacing: 2px;
`;

const SubmitButton = styled.button`
    background: rgba(186, 29, 29, 1);
    color: white;
    font-family: 'Montserrat', sans-serif;
    border: none;
    border-radius: 20px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    margin-top: 15px;
    &:hover {
        background: rgba(186, 29, 29, 0.8);
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    font-size: 22px;
    cursor: pointer;
    color: rgba(41, 37, 97, 1);
`;

const AnchorButton = styled.a`
    display: inline-block;
    font-size: 14px;
    font-weight: 500;
    color: rgba(41, 37, 97, 1);
    cursor: pointer;
    text-decoration: underline;

    &:hover {
        color: rgba(41, 37, 97, 0.8);
    }
`;

// Function to mask email, showing the first 2 and last 2 letters, with asterisks in between
const maskEmail = (email) => {
    const [localPart, domain] = email.split('@');

    // Mask the local part to show first 2 and last 2 characters with asterisks in the middle
    const firstTwo = localPart.slice(0, 2);
    const lastTwo = localPart.slice(-2);
    const maskedLocalPart = `${firstTwo}${'*'.repeat(localPart.length - 4)}${lastTwo}`;

    return `${maskedLocalPart}@${domain}`;
};

// Function to mask mobile number with exactly 5 asterisks
const maskMobile = (mobile) => {
    // Show the first 3 digits and last 2 digits
    const firstThreeDigits = mobile.slice(0, 3);
    const lastTwoDigits = mobile.slice(-2);
    const maskedMobile = `${firstThreeDigits}*****${lastTwoDigits}`; // Mask the middle part with 5 asterisks
    return maskedMobile;
};

// Modal component
export default function OtpModalComponent({
    showModal,
    onClose,
    response,
    formData,
    setFormData,
    handleFormSubmit,
    handleOtpSubmit,
    handleShowData,
    setLoading,
    claimsData,
    updateClaimsData
}) {
    const [otp, setOtp] = useState('');
    const [error, setError] = useState('');
    const [invalid, setInvalid] = useState('');
    const [showAnchorButton, setShowAnchorButton] = useState(true);
    const [isInputDisabled, setInputDisabled] = useState(false);

    const email = response?.response?.email;
    const mobile = response?.response?.mobileNo;
    let type = response?.response?.type;

    const maskedEmail = email ? maskEmail(email) : '';
    const maskedMobile = mobile ? maskMobile(mobile) : '';

    const handleChangeType = (e) => {
        handleFormSubmit(e, 'mobile');
        type = 'mobile';
        setShowAnchorButton(false);
    };

    const handleGenerate = (e) => {
        handleFormSubmit(e, 'email');
        type = 'email';
        setOtp('');
        setError('');
        setInvalid('');
        setInputDisabled(false);
        setShowAnchorButton(true);
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        // Allow only numbers and limit length to 6 digits
        if (/^\d*$/.test(value) && value.length <= 6) {
            setOtp(value);
        }
    };

    const handleSubmit = async () => {
        if (!otp) {
            setError('OTP is required.');
        } else if (!/^\d{6}$/.test(otp)) {
            setError('OTP must be 6 digits.');
        } else {
            setLoading(true);
            const otpResult = await handleOtpSubmit(type, otp);

            if (otpResult?.status === 'success') {
                setError('');
                setLoading(false);
                handleShowData(otpResult);
                onClose();
                updateClaimsData({ ...claimsData, otpData: { isOtpValidated: true, timestamp: null } });
            } else if (
                otpResult?.status === 'failed' &&
                otpResult?.message === 'OTP Mismatch. OTP Count exeeds max. OTP set to expired '
            ) {
                setInvalid('Click here to generate');
                setError('');
                setOtp('');
                setLoading(false);
                setInputDisabled(true);
            } else {
                setError('Invalid OTP');
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        if (showModal) {
            setOtp('');
            setError('');
            setInvalid('');
        }
    }, [showModal]);

    if (!showModal) return null; // If modal is not visible, don't render anything

    return (
        <ModalBackground>
            <ModalContainer>
                <CloseButton onClick={onClose}>&times;</CloseButton>
                <StyledModalHeader>OTP Verification</StyledModalHeader>
                {type === 'email' ? (
                    <DescriptionText>
                        Enter the 6-digit verification code that was sent to your {type}: {maskedEmail}
                    </DescriptionText>
                ) : (
                    <DescriptionText>
                        Enter the 6-digit verification code that was sent to your {type}: {maskedMobile}
                    </DescriptionText>
                )}
                {invalid && (
                    <DescriptionText style={{ color: 'red', cursor: 'pointer', fontStyle: 'italic', margin: '0', padding: '0' }} onClick={handleGenerate}>
                        {invalid}
                    </DescriptionText>
                )}
                <Row>
                    <Col>
                        <StyledInput
                            type="text"
                            placeholder="Enter OTP"
                            maxLength={6}
                            value={otp ?? ''}
                            onChange={handleInputChange}
                            disabled={isInputDisabled}
                        />
                        {error && <DescriptionText style={{ color: 'red' }}>{error}</DescriptionText>}
                    </Col>
                </Row>
                {showAnchorButton && (
                    <Row>
                        <Col>
                            <AnchorButton onClick={(e) => handleChangeType(e)}>Use mobile number instead</AnchorButton>
                        </Col>
                    </Row>
                )}
                <Row>
                    <Col>
                        <SubmitButton onClick={handleSubmit}>Verify</SubmitButton>
                    </Col>
                </Row>
            </ModalContainer>
        </ModalBackground>
    );
}
